//------------------------------------------------------------------------
// Customized version of Flickity
//
// Issues pertaining to accessibility:
// https://github.com/mozilla-rally/site/issues/262
// https://github.com/mozilla-rally/site/issues/278
//------------------------------------------------------------------------
import Flickity from 'flickity';

/*
  To improve accessibility for screen reader users, we need to focus the selected
  cell after it has settled. Focusing the cell _before_ it has settled causes it
  to jump to the new position and messes up Flickity’s transitions, causing jank.
  We could prevent most browsers from scrolling on focus using the “preventScroll: true”
  option, but unfortunately it’s not supported in Safari or iOS Safari yet.
  To determine whether the selected slide should be focused after it settles,
  we’re adding a custom option variable called “shouldFocus”. This will be set to
  “true” for any user-initiated action (i.e. not autoplay) and reset to “false”
  after the carousel has settled (https://flickity.metafizzy.co/events.html#settle).
  We added a custom option because we don’t have access to the carousel instances
  in the Flickity library, since it’s a factory that builds new carousels.
*/
Flickity.defaults.shouldFocus = false;

// Override the default arrow key event handlers to update the “shouldFocus” option
// https://github.com/metafizzy/flickity/blob/fb96a2400f2349b624f098cab6869ba53c0c5c18/js/flickity.js#L835-L848
Flickity.keyboardHandlers = {
  // left arrow
  37: function() {
    var leftMethod = this.options.rightToLeft ? 'next' : 'previous';
    this.uiChange();
    this.options.shouldFocus = true;// Note: We added this line
    this[ leftMethod ]();
  },
  // right arrow
  39: function() {
    var rightMethod = this.options.rightToLeft ? 'previous' : 'next';
    this.uiChange();
    this.options.shouldFocus = true;// Note: We added this line
    this[ rightMethod ]();
  },
};

/*
  Override the “onkeydown” handler to enable keyboard navigation
  when an individual slide is focused, not just the entire carousel,
  which is the default functionality. This allows users to continue
  navigating using the arrow keys after a slide has been focused.
  https://github.com/mozilla-rally/site/issues/278
  https://github.com/metafizzy/flickity/blob/master/js/flickity.js#L819
*/
Flickity.prototype.onkeydown = function(event) {
  // only work if element is in focus
  var isNotFocused =
    document.activeElement &&
    // NOTE: This is the only line we changed
    !(document.activeElement == this.element || this.element.contains(document.activeElement));

  if ( !this.options.accessibility || isNotFocused ) {
    return;
  }

  var handler = Flickity.keyboardHandlers[ event.keyCode ];

  if ( handler ) {
    handler.call( this );
  }
};

/*
  Enable equal-height slides with CSS by adding a class after Flickity resizes
  https://github.com/metafizzy/flickity/issues/534#issuecomment-328859144
  https://codepen.io/desandro/pen/ZXEGVq
*/
const resize = Flickity.prototype.resize;
Flickity.prototype.resize = function() {
  this.element.classList.remove('flickity-resize');
  resize.call(this);
  this.element.classList.add('flickity-resize');
};

export default Flickity;
